import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../common/Button';
import VoiceInput from '../common/VoiceInput';
import { useReadingsStore } from '../../store/readingsStore';
import { SpreadType } from '../../types';

const ReadingForm: React.FC = () => {
  const navigate = useNavigate();
  const { setCurrentReading } = useReadingsStore();
  const [step, setStep] = useState<'choose' | 'question' | 'cards'>('choose');
  const [spreadType, setSpreadType] = useState<SpreadType>('1-card');
  const [subject, setSubject] = useState('');
  const [question, setQuestion] = useState('');
  const [context, setContext] = useState('');
  const [cards, setCards] = useState<Array<{ name: string; position: string; isReversed: boolean }>>([]);
  const [userInterpretation, setUserInterpretation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stepNumber = step === 'choose' ? 1 : step === 'question' ? 2 : 3;

  const handleSpreadTypeSelect = (type: SpreadType) => {
    setSpreadType(type);
    setStep('question');
  };

  const handleQuestionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (subject && question && context) {
      const positions = getSpreadPositions(spreadType);
      setCards(positions.map(position => ({
        name: '',
        position,
        isReversed: false
      })));
      setStep('cards');
    }
  };

  const handleCardChange = (index: number, value: string) => {
    const updatedCards = [...cards];
    updatedCards[index] = {
      ...updatedCards[index],
      name: value
    };
    setCards(updatedCards);
  };

  const handleReversalToggle = (index: number) => {
    const updatedCards = [...cards];
    updatedCards[index] = {
      ...updatedCards[index],
      isReversed: !updatedCards[index].isReversed
    };
    setCards(updatedCards);
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      // Validate all required fields
      if (!subject || !context || !spreadType || !cards.every(card => card.name)) {
        throw new Error('Please fill in all required fields');
      }

      // Set the current reading in the store
      await setCurrentReading({
        subject,
        question,
        context,
        spreadType,
        cards,
        userInterpretation,
        is_virtual: false
      });

      // Navigate to interpretation view
      navigate('/interpretation');
    } catch (error) {
      console.error('Error submitting reading:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
      setIsLoading(false);
    }
  };

  const getSpreadPositions = (type: SpreadType): string[] => {
    switch (type) {
      case '1-card':
        return ['Single Card'];
      case '3-card':
        return ['Past', 'Present', 'Future'];
      case '5-card':
        return ['Center', 'Above', 'Below', 'Left', 'Right'];
      case 'celtic-cross':
        return [
          'Present', 'Challenge', 'Past', 'Future',
          'Above', 'Below', 'Advice', 'Environment',
          'Hopes/Fears', 'Outcome'
        ];
      case 'virtual-daily-card':
        return ['Daily Card'];
      case 'two-options':
        return [
          'What Grounds You', 'Your Key Motivation', 'Your Ideal Outcome', 'About Option 1',
          'Option 1 Outcome', 'About Option 2', 'Option 2 Outcome'
        ];
      case 'relationship':
        return [
          'You', 'Partner', 'Relationship Dynamic', 'Challenges',
          'Strengths', 'Potential', 'Advice'
        ];
      default:
        return ['Single Card'];
    }
  };

  const spreads = [
    { type: '1-card' as SpreadType, name: 'Single Card', description: 'Draw a single card for guidance and insight.' },
    { type: '3-card' as SpreadType, name: 'Three Card Spread', description: 'One of the most common spreads to get more in depth guidance for a question or situation you have in mind.' },
    { type: '5-card' as SpreadType, name: 'Five Card Spread', description: 'A more complex reading that explores a situation or question you have while considering what other influences may be at play.' },
    { type: 'celtic-cross' as SpreadType, name: 'Celtic Cross', description: 'A comprehensive ten-card spread for deep insight into complex situations.' },
    { type: 'two-options' as SpreadType, name: 'Two Options', description: 'Compare two different paths or decisions to help you choose the best option.' },
    { type: 'relationship' as SpreadType, name: 'Relationship', description: 'Explore the dynamics, challenges, and potential of a relationship.' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="max-w-4xl mx-auto"
    >
      {/* Step indicator */}
      <p className="text-xs tracking-[0.08em] uppercase text-mist mb-6">
        Step {stepNumber} of 3
      </p>

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-3 rounded-md flex items-center gap-2">
          <span>{error}</span>
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* Step 1: Spread Selection */}
        {step === 'choose' && (
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="space-y-8"
          >
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-semibold text-ink dark:text-parchment">
                New Tarot Reading
              </h1>
              <p className="text-ink/70 dark:text-parchment/60 mt-3">
                Begin your reading by choosing a spread layout. Each spread offers different insights and perspectives.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {spreads.map((spread, index) => (
                <motion.button
                  key={spread.type}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  onClick={() => handleSpreadTypeSelect(spread.type)}
                  className="bg-vellum dark:bg-shadow rounded-xl border border-mist dark:border-dusk p-8 text-left hover:border-sun dark:hover:border-ember hover:-translate-y-px transition-all duration-200"
                >
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-sun dark:text-ember">
                    {spread.name}
                  </h3>
                  <p className="text-sm text-ink/70 dark:text-parchment/60 mt-2">
                    {spread.description}
                  </p>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 2: Question Form */}
        {step === 'question' && (
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <form onSubmit={handleQuestionSubmit} className="max-w-2xl mx-auto space-y-8">
              <div>
                <h1 className="font-display text-4xl md:text-5xl font-semibold text-ink dark:text-parchment">
                  What Would You Like to Explore?
                </h1>
                <p className="text-ink/70 dark:text-parchment/60 mt-3">
                  Take a moment to formulate your question. The clearer your intention, the more meaningful your reading will be.
                </p>
              </div>

              <div className="space-y-5">
                <div>
                  <label htmlFor="subject" className="block text-xs font-medium uppercase tracking-[0.08em] text-ink/70 dark:text-parchment/60 mb-2">
                    Who is this reading for?
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-3 text-base py-3 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-sun focus:border-sun bg-vellum dark:bg-shadow transition-colors duration-200"
                    placeholder="e.g., Me, Friend, Client"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="question" className="block text-xs font-medium uppercase tracking-[0.08em] text-ink/70 dark:text-parchment/60 mb-2">
                    Your Question
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="question"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      className="w-full px-3 text-base py-3 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-sun focus:border-sun bg-vellum dark:bg-shadow transition-colors duration-200"
                      placeholder="What would you like guidance about?"
                      required
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <VoiceInput
                        onTranscript={(text) => setQuestion(prev => prev + (prev ? ' ' : '') + text)}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="context" className="block text-xs font-medium uppercase tracking-[0.08em] text-ink/70 dark:text-parchment/60 mb-2">
                    Context
                  </label>
                  <div className="relative">
                    <textarea
                      id="context"
                      value={context}
                      onChange={(e) => setContext(e.target.value)}
                      className="w-full px-3 text-base py-3 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-sun focus:border-sun bg-vellum dark:bg-shadow transition-colors duration-200"
                      rows={3}
                      placeholder="Provide some context about your question or situation"
                      required
                    />
                    <div className="absolute right-2 top-2">
                      <VoiceInput
                        onTranscript={(text) => setContext(prev => prev + (prev ? ' ' : '') + text)}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep('choose')}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!subject || !question || !context}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>
        )}

        {/* Step 3: Card Entry */}
        {step === 'cards' && (
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="space-y-8"
          >
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-semibold text-ink dark:text-parchment">
                Enter Your Cards
              </h1>
              <p className="text-ink/70 dark:text-parchment/60 mt-3">
                Enter each card as it appears in your spread.
              </p>
            </div>

            <div className="space-y-5">
              {cards.map((card, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.06 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex-grow relative">
                    <label className="block font-display italic text-base text-sun dark:text-ember mb-2">
                      {card.position}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={card.name}
                        onChange={(e) => handleCardChange(index, e.target.value)}
                        className="w-full px-3 text-base py-3 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-sun focus:border-sun bg-vellum dark:bg-shadow transition-colors duration-200"
                        placeholder="Enter card name"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <VoiceInput
                          onTranscript={(text) => handleCardChange(index, text)}
                        />
                      </div>
                    </div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer mt-8">
                    <input
                      type="checkbox"
                      checked={card.isReversed}
                      onChange={() => handleReversalToggle(index)}
                      className="sr-only peer"
                    />
                    <div className="relative w-10 h-5 bg-mist dark:bg-dusk rounded-full peer peer-checked:bg-sun after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 rtl:peer-checked:after:-translate-x-5"></div>
                    <span className="ms-2 text-xs uppercase tracking-[0.08em] text-ink/50 dark:text-parchment/60">Reversed</span>
                  </label>
                </motion.div>
              ))}
            </div>

            <div>
              <label htmlFor="interpretation" className="block font-display italic text-base text-ink dark:text-parchment mb-2">
                What do you see in the cards?
              </label>
              <div className="relative">
                <textarea
                  id="interpretation"
                  value={userInterpretation}
                  onChange={(e) => setUserInterpretation(e.target.value)}
                  className="w-full px-3 text-base py-3 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-sun focus:border-sun bg-vellum dark:bg-shadow transition-colors duration-200"
                  rows={4}
                  placeholder="Share your thoughts on what the cards are telling you"
                />
                <div className="absolute right-2 top-2">
                  <VoiceInput
                    onTranscript={(text) => setUserInterpretation(prev => prev + (prev ? ' ' : '') + text)}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep('question')}
                disabled={isLoading}
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                isLoading={isLoading}
                disabled={!cards.every(card => card.name) || isLoading}
              >
                Get Interpretation
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ReadingForm;
